/**
 * Index Calculation Engine
 * Implements the official IQUP workbook structure on top of aggregate data.
 */

export function thresholdToScore(value, { low, high, factor = 5, invert = false }) {
  let score
  if (value <= low) {
    score = 0
  } else if (value > high) {
    score = 2
  } else {
    score = 1
  }

  if (invert) score = 2 - score
  return score * factor
}

export function bestOfThree({ high, mid, low }, { threshold = 50, factor = 5 }) {
  let score
  if (high >= threshold) {
    score = 2
  } else if (mid >= threshold) {
    score = 1
  } else if (low >= threshold) {
    score = 0
  } else if (high >= mid && high >= low) {
    score = 2
  } else if (mid >= high && mid >= low) {
    score = 1
  } else {
    score = 0
  }

  return score * factor
}

function weightedAverage(items) {
  const totalWeight = items.reduce((sum, item) => sum + item.weight, 0)
  if (totalWeight === 0) return 0

  const weightedSum = items.reduce((sum, item) => sum + item.value * item.weight, 0)
  return weightedSum / totalWeight
}

function round(value) {
  return Math.round(value * 100) / 100
}

function calculateParkingScore(agg, threshold) {
  if (typeof threshold === 'object' && threshold !== null) {
    const parkingScores = [
      thresholdToScore(agg.estacionamento_meio_fio_pct, { ...threshold, factor: 5 }),
      thresholdToScore(agg.estacionamento_zona_azul_pct, { ...threshold, factor: 5 }),
      thresholdToScore(agg.estacionamento_recuo_pct, { ...threshold, factor: 5 }),
      thresholdToScore(agg.estacionamento_garagem_pct, { ...threshold, factor: 5 }),
      thresholdToScore(agg.estacionamento_moto_pct, { ...threshold, factor: 5 })
    ]

    return parkingScores.reduce((sum, value) => sum + value, 0) / parkingScores.length
  }

  const parkingScores = [
    agg.estacionamento_meio_fio_pct > threshold ? 10 : 0,
    agg.estacionamento_zona_azul_pct > threshold ? 10 : 0,
    agg.estacionamento_recuo_pct > threshold ? 10 : 0,
    agg.estacionamento_garagem_pct > threshold ? 10 : 0,
    agg.estacionamento_moto_pct > threshold ? 10 : 0
  ]

  return parkingScores.reduce((sum, value) => sum + value, 0) / parkingScores.length
}

function calculateTravessiaScore(agg, config) {
  const semaforoPedestres = thresholdToScore(agg.semaforo_pedestres_pct, {
    ...config.semaforosThresholds,
    factor: 5
  })
  const semaforoVeiculos = thresholdToScore(agg.semaforo_veiculos_pct, {
    ...config.semaforosThresholds,
    factor: 5
  })
  const faixaPedestres = thresholdToScore(agg.faixa_pedestres_pct, {
    ...config.faixaThresholds,
    factor: 5
  })

  return weightedAverage([
    { value: semaforoPedestres, weight: 1 },
    { value: semaforoVeiculos, weight: 1 },
    { value: faixaPedestres, weight: 1 }
  ])
}

function calculateTestadaScore(agg, config) {
  const { large, medium } = config.testadaThresholds

  if (agg.testada_grande_pct >= large) return 0
  if (agg.testada_grande_pct >= medium) return 5
  return 10
}

function calculateNodal(agg, config) {
  return weightedAverage([
    { value: agg.equipamento_urbano ? 10 : 0, weight: config.indicatorWeights.equipamentoUrbano },
    { value: agg.terminal_de_transporte ? 10 : 0, weight: config.indicatorWeights.terminalTransporte }
  ])
}

function calculateAccess(agg, config) {
  const busScore = config.busScore[agg.onibus] ?? config.busScore.nao ?? 0
  const parkingScore = calculateParkingScore(agg, config.parkingThresholds || config.parkingThreshold)
  const bikeScore = agg.has_ciclovia ? 10 : 0
  const parkingWeight = config.neutralizeCarAccessForPedestrianStreets && agg.neutralize_car_access
    ? 0
    : config.indicatorWeights.estacionamento

  const valor = weightedAverage([
    { value: busScore, weight: config.indicatorWeights.onibus },
    { value: parkingScore, weight: parkingWeight },
    { value: bikeScore, weight: config.indicatorWeights.bicicleta }
  ])

  return {
    Valor: round(valor),
    Componentes: {
      'Acesso por Onibus': busScore,
      Estacionamento: parkingScore,
      Bicicleta: bikeScore
    }
  }
}

export function calculateConnectivity(agg, config) {
  const nodal = calculateNodal(agg, config.components.nodal)
  const access = calculateAccess(agg, config.components.access)

  const valor = weightedAverage([
    { value: nodal, weight: config.components.nodal.weight },
    { value: access.Valor, weight: config.components.access.weight }
  ])

  return {
    Valor: round(valor),
    Componentes: {
      'Ponto Nodal': round(nodal),
      Acessos: access
    }
  }
}

function calculateUsos(agg, config) {
  const superiorServices = thresholdToScore(agg.uso_superior_pct, {
    ...config.superiorThresholds,
    factor: 5
  })
  const horarioNoturno = thresholdToScore(agg.apos_19_pct, {
    ...(config.noturnoThresholds || config.horarioThresholds),
    factor: 5
  })
  const fimDeSemana = thresholdToScore(agg.fim_de_semana_pct, {
    ...config.fdsThresholds,
    factor: 5
  })

  let terreoServices
  if (agg.uso_terreo_unique_count <= config.terreoBreakpoints.low) {
    terreoServices = 0
  } else if (agg.uso_terreo_unique_count <= config.terreoBreakpoints.high) {
    terreoServices = 5
  } else {
    terreoServices = 10
  }

  const buildingUseWeights = config.buildingUseWeights || { terreo: 1, superior: 1 }
  const usoEdificacao = weightedAverage([
    { value: terreoServices, weight: buildingUseWeights.terreo },
    { value: superiorServices, weight: buildingUseWeights.superior }
  ])

  const valor = weightedAverage([
    { value: usoEdificacao, weight: config.indicatorWeights.usoEdificacao },
    { value: horarioNoturno, weight: config.indicatorWeights.horarioNoturno },
    { value: fimDeSemana, weight: config.indicatorWeights.fimDeSemana }
  ])

  return {
    Valor: round(valor),
    Componentes: {
      'Uso Edificacao': round(usoEdificacao),
      'Servicos no Terreo': terreoServices,
      'Pavimento Superior': superiorServices,
      'Horario Noturno': horarioNoturno,
      'Fim de Semana': fimDeSemana
    }
  }
}

function calculateGabaritoScore(agg, config) {
  const thresholds = config.gabaritoThresholds
  const lowRise = agg.gabarito_distribution.high
  const midRise = agg.gabarito_distribution.mid
  const tall = agg.gabarito_distribution.low

  if ('tallHigh' in thresholds) {
    if (tall > thresholds.tallHigh) return 0
    if (lowRise > thresholds.lowRiseLow) return 10
    if (midRise >= thresholds.midLow && midRise <= (thresholds.midHigh ?? thresholds.tallHigh)) return 5
    return 0
  }

  const values = [tall, midRise, lowRise]
  if (values.some(value => value < thresholds.low)) return 0
  if (values.some(value => value > thresholds.high)) return 5
  return 10
}

function calculateIdadeScore(agg, config) {
  const breakpoints = config.idadeBreakpoints
  const groups = new Set()

  for (const ages of agg.idade_por_lote || []) {
    for (const age of ages) {
      if (age <= breakpoints.young) {
        groups.add('young')
      } else if (age <= breakpoints.old) {
        groups.add('mid')
      } else {
        groups.add('old')
      }
    }
  }

  if (groups.size >= 3) return 10
  if (groups.size === 2) return 5
  return 0
}

function calculateArquitetura(agg, config) {
  const gabarito = calculateGabaritoScore(agg, config)
  const testada = calculateTestadaScore(agg, config)
  const idade = calculateIdadeScore(agg, config)

  const valor = weightedAverage([
    { value: gabarito, weight: config.indicatorWeights.gabarito },
    { value: testada, weight: config.indicatorWeights.testada },
    { value: idade, weight: config.indicatorWeights.idade }
  ])

  return {
    Valor: round(valor),
    Componentes: {
      Gabarito: gabarito,
      Testada: testada,
      Idade: idade
    }
  }
}

export function calculateDiversity(agg, config) {
  const usos = calculateUsos(agg, config.components.uses)
  const arquitetura = calculateArquitetura(agg, config.components.architecture)

  const valor = weightedAverage([
    { value: usos.Valor, weight: config.components.uses.weight },
    { value: arquitetura.Valor, weight: config.components.architecture.weight }
  ])

  return {
    Valor: round(valor),
    Componentes: {
      Usos: usos,
      Arquitetura: arquitetura
    }
  }
}

function calculateSensorial(agg, config) {
  const arvores = thresholdToScore(agg.arborizacao_pct, { ...config.arborizacaoThresholds, factor: 5 })
  const protecao = thresholdToScore(agg.protecao_sol_chuva_pct, { ...config.protecaoThresholds, factor: 5 })
  const limpeza = config.limpezaThresholds
    ? thresholdToScore(agg.limpeza_distribution.otima, { ...config.limpezaThresholds, factor: 5 })
    : bestOfThree(
      { high: agg.limpeza_distribution.otima, mid: agg.limpeza_distribution.boa, low: agg.limpeza_distribution.ruim },
      { factor: 5 }
    )
  const muros = thresholdToScore(agg.muro_pct, { ...config.murosThresholds, factor: 5, invert: true })
  const fachada = config.fachadaThresholds
    ? thresholdToScore(agg.conservacao_fachada_distribution.otimo, { ...config.fachadaThresholds, factor: 5 })
    : bestOfThree(
      { high: agg.conservacao_fachada_distribution.otimo, mid: agg.conservacao_fachada_distribution.bom, low: agg.conservacao_fachada_distribution.ruim },
      { factor: 5 }
    )
  const travessia = calculateTravessiaScore(agg, config)

  const valor = weightedAverage([
    { value: arvores, weight: config.indicatorWeights.arvores },
    { value: protecao, weight: config.indicatorWeights.protecao },
    { value: limpeza, weight: config.indicatorWeights.limpeza },
    { value: muros, weight: config.indicatorWeights.muros },
    { value: fachada, weight: config.indicatorWeights.fachada },
    { value: travessia, weight: config.indicatorWeights.travessia }
  ])

  return {
    Valor: round(valor),
    Componentes: {
      Arborizacao: arvores,
      'Protecao Sol/Chuva': protecao,
      Limpeza: limpeza,
      Muro: muros,
      Fachada: fachada,
      Travessia: round(travessia)
    }
  }
}

function calculatePasseio(agg, config) {
  const largura = bestOfThree(
    { high: agg.largura_calcada_distribution.wide, mid: agg.largura_calcada_distribution.medium, low: agg.largura_calcada_distribution.narrow },
    { threshold: config.larguraThreshold, factor: 5 }
  )
  const faixa = thresholdToScore(agg.faixa_livre_pedestres_pct, { ...config.faixaThresholds, factor: 5 })
  const piso = thresholdToScore(agg.piso_acessivel_pct, { ...config.guiasThresholds, factor: 5 })
  const conservacao = config.calcadaThresholds
    ? thresholdToScore(agg.conservacao_calcada_distribution.otimo, { ...config.calcadaThresholds, factor: 5 })
    : bestOfThree(
      { high: agg.conservacao_calcada_distribution.otimo, mid: agg.conservacao_calcada_distribution.bom, low: agg.conservacao_calcada_distribution.ruim },
      { factor: 5 }
    )
  const degraus = thresholdToScore(agg.degrau_entre_calcadas_pct, {
    ...config.degrausThresholds,
    factor: 5,
    invert: true
  })
  const entrada = thresholdToScore(agg.entrada_de_veiculos_pct, {
    ...config.entradasThresholds,
    factor: 5,
    invert: true
  })

  const valor = weightedAverage([
    { value: largura, weight: config.indicatorWeights.largura },
    { value: faixa, weight: config.indicatorWeights.faixa },
    { value: piso, weight: config.indicatorWeights.piso },
    { value: conservacao, weight: config.indicatorWeights.conservacao },
    { value: degraus, weight: config.indicatorWeights.degraus },
    { value: entrada, weight: config.indicatorWeights.entrada }
  ])

  return {
    Valor: round(valor),
    Componentes: {
      'Largura Calcada': largura,
      'Faixa Livre': faixa,
      Piso: piso,
      'Conservacao Calcada': conservacao,
      Degraus: degraus,
      Entrada: entrada
    }
  }
}

export function calculateComfort(agg, config) {
  const sensorial = calculateSensorial(agg, config.components.sensorial)
  const passeio = calculatePasseio(agg, config.components.passeio)

  const valor = weightedAverage([
    { value: sensorial.Valor, weight: config.components.sensorial.weight },
    { value: passeio.Valor, weight: config.components.passeio.weight }
  ])

  return {
    Valor: round(valor),
    Componentes: {
      Sensorial: sensorial,
      Passeio: passeio
    }
  }
}

export function calculateIndex(polo, config) {
  const connectivity = calculateConnectivity(polo.aggregates, config.connectivity)
  const diversity = calculateDiversity(polo.aggregates, config.diversity)
  const comfort = calculateComfort(polo.aggregates, config.comfort)

  const valor = weightedAverage([
    { value: connectivity.Valor, weight: config.connectivity.weight },
    { value: diversity.Valor, weight: config.diversity.weight },
    { value: comfort.Valor, weight: config.comfort.weight }
  ])

  return {
    Valor: round(valor),
    Componentes: {
      Conectividade: connectivity,
      Diversidade: diversity,
      Conforto: comfort
    }
  }
}

export function calculateAllPolos(polos, config) {
  return polos
    .map(polo => ({
      ...polo,
      indicadores: calculateIndex(polo, config)
    }))
    .sort((a, b) => b.indicadores.Valor - a.indicadores.Valor)
}

export function getWeightDistribution(config) {
  const mainWeights = {
    connectivity: config.connectivity.weight,
    diversity: config.diversity.weight,
    comfort: config.comfort.weight
  }

  const mainTotal = mainWeights.connectivity + mainWeights.diversity + mainWeights.comfort
  const connTotal = config.connectivity.components.nodal.weight + config.connectivity.components.access.weight
  const divTotal = config.diversity.components.uses.weight + config.diversity.components.architecture.weight
  const comfTotal = config.comfort.components.sensorial.weight + config.comfort.components.passeio.weight

  return {
    main: {
      connectivity: mainWeights.connectivity / mainTotal,
      diversity: mainWeights.diversity / mainTotal,
      comfort: mainWeights.comfort / mainTotal
    },
    connectivity: {
      nodal: config.connectivity.components.nodal.weight / connTotal,
      access: config.connectivity.components.access.weight / connTotal
    },
    diversity: {
      uses: config.diversity.components.uses.weight / divTotal,
      architecture: config.diversity.components.architecture.weight / divTotal
    },
    comfort: {
      sensorial: config.comfort.components.sensorial.weight / comfTotal,
      passeio: config.comfort.components.passeio.weight / comfTotal
    }
  }
}
